import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { map, filter, take, switchMap, first, catchError } from 'rxjs/operators';
import * as i1 from '@onep/front-common-lib/event-bus';
import { ChannelType } from '@onep/front-common-lib/event-bus';
import { CommonModule } from '@angular/common';
import * as i1$1 from 'apollo-angular';
import { gql } from 'apollo-angular';
import * as i1$2 from '@angular/router';
import { TranslocoModule, TRANSLOCO_CONFIG, translocoConfig, getBrowserLang, TRANSLOCO_LOADER } from '@ngneat/transloco';
import { of, combineLatest } from 'rxjs';
import * as i1$3 from '@angular/common/http';

// eslint-disable-next-line no-shadow
var AuthInfoType;
(function (AuthInfoType) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    AuthInfoType["AUTHENTICATED"] = "AUTHENTICATED";
    // eslint-disable-next-line @typescript-eslint/naming-convention
    AuthInfoType["NOT_AUTHENTICATED"] = "NOT_AUTHENTICATED";
})(AuthInfoType || (AuthInfoType = {}));
/**
 * Check if the auth info is about an authenticated user.
 *
 * @param data the data.
 */
const isAuthenticated = (data) => data.type === AuthInfoType.AUTHENTICATED;

const EVENTS_CHANNELS = {
    authChannel: [
        'shared-info',
        'auth',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    logoutChannel: [
        'shared-info',
        'logout',
        {
            type: ChannelType.event,
        },
    ],
    resetPasswordChannel: [
        'shared-info',
        'reset-password',
        {
            type: ChannelType.event,
        },
    ],
    resetPhoneChannel: [
        'shared-info',
        'reset-phone',
        {
            type: ChannelType.event,
        },
    ],
    adminRightChannel: [
        'shared-info',
        'admin-ui-rights',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    currentOrganizationChannel: [
        'shared-info',
        'current-organization',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    changeOrganizationChannel: [
        'shared-info',
        'change-organization',
        {
            type: ChannelType.event,
        },
    ],
    routeHomeChannel: [
        'menu',
        'route-home',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    localeChannel: [
        'shared-info',
        'locale',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    customizationDataChannel: [
        'shared-info',
        'customization-data',
        {
            type: ChannelType.replayLastValue,
        },
    ],
    trackingChannel: [
        'shared-info',
        'tracking',
        {
            type: ChannelType.event,
        },
    ],
};

class AuthService {
    constructor(eventBus) {
        this.currentAccessToken = null;
        this.logoutChannel = eventBus.connectToChannel(...EVENTS_CHANNELS.logoutChannel);
        this.resetPasswordChannel = eventBus.connectToChannel(...EVENTS_CHANNELS.resetPasswordChannel);
        this.resetPhoneChannel = eventBus.connectToChannel(...EVENTS_CHANNELS.resetPhoneChannel);
        this.accessToken$ = eventBus
            .connectToChannel(...EVENTS_CHANNELS.authChannel)
            .receiver.pipe(map((info) => (isAuthenticated(info) ? info.accessToken : null)));
        this.subscription = this.accessToken$.subscribe((token) => {
            this.currentAccessToken = token;
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Get the current access token
     * or null if the user is not authenticated.
     */
    getCurrentAccessToken() {
        return this.currentAccessToken;
    }
    /**
     * Returns an observable that waits for the auth being fetched.
     * (similar to what the AuthGuard does).
     */
    waitForAuthentication() {
        return this.accessToken$.pipe(filter((token) => token != null), map(() => { }), take(1));
    }
    /**
     * Logout of the authentication.
     */
    logout() {
        this.logoutChannel.emitter.next(true);
    }
    /**
     * If the access token is not value anymore and no automatic renew was done.
     * It tries to go to the auth ui to do an automatic internal SSO renewal.
     */
    tryInternalSso() {
        this.logoutChannel.emitter.next(false);
    }
    resetPassword(sutToken) {
        this.resetPasswordChannel.emitter.next(sutToken);
    }
    resetPhone(sutToken) {
        this.resetPhoneChannel.emitter.next(sutToken);
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService, deps: [{ token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.EventBusService }]; } });

/**
 * Guard to put on all microfront routes to prevent
 * them from loading until an access token is available.
 */
class AuthGuard {
    constructor(authService) {
        this.authService = authService;
    }
    canActivate(route, state) {
        return this.getBlockingObservable();
    }
    canActivateChild(childRoute, state) {
        return this.getBlockingObservable();
    }
    getBlockingObservable() {
        return this.authService.waitForAuthentication().pipe(map(() => true));
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthGuard, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

/**
 * Module providing the AuthService and auth guards.
 * When importing this module, please import the EventBusModule too
 * (with forRoot() your root module).
 */
class AuthModule {
    static forRoot() {
        return {
            ngModule: AuthModule,
            providers: [AuthGuard, AuthService],
        };
    }
}
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, imports: [CommonModule] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                    providers: [],
                }]
        }] });

class CurrentOrganizationService {
    constructor(eventBusService) {
        this.eventBusService = eventBusService;
        this.currentOrganizationId$ = this.eventBusService
            .connectToChannel(...EVENTS_CHANNELS.currentOrganizationChannel)
            .receiver.pipe(map((info) => info.currentOrganizationId));
        this.changeOrgChannel =
            this.eventBusService.connectToChannel(...EVENTS_CHANNELS.changeOrganizationChannel);
    }
    /**
     * Ask to switch organization.
     *
     * @param organizationId the organization id.
     */
    switchOrganization(organizationId) {
        this.changeOrgChannel.emitter.next({
            organizationId,
        });
    }
    /**
     * Returns an observable that emits the selected organization id
     * when it's ready.
     * (Similar to what CurrentOrganizationResolver returns).
     */
    waitForOrganization() {
        return this.currentOrganizationId$.pipe(take(1));
    }
}
CurrentOrganizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationService, deps: [{ token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
CurrentOrganizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.EventBusService }]; } });

/**
 * Resolver that resolves the current organization id.
 */
class CurrentOrganizationResolver {
    constructor(currentOrganizationService) {
        this.currentOrganizationService = currentOrganizationService;
    }
    resolve(route, state) {
        return this.currentOrganizationService.waitForOrganization();
    }
}
CurrentOrganizationResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationResolver, deps: [{ token: CurrentOrganizationService }], target: i0.ɵɵFactoryTarget.Injectable });
CurrentOrganizationResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationResolver });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CurrentOrganizationResolver, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CurrentOrganizationService }]; } });

class OrganizationModule {
    static forRoot() {
        return {
            ngModule: OrganizationModule,
            providers: [CurrentOrganizationResolver, CurrentOrganizationService],
        };
    }
}
OrganizationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
OrganizationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: OrganizationModule, imports: [CommonModule] });
OrganizationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                }]
        }] });

var AccountStatus;
(function (AccountStatus) {
    AccountStatus["Completed"] = "COMPLETED";
    AccountStatus["NotAuthorized"] = "NOT_AUTHORIZED";
    AccountStatus["PendingValidation"] = "PENDING_VALIDATION";
    AccountStatus["Rejected"] = "REJECTED";
    AccountStatus["Uncompleted"] = "UNCOMPLETED";
})(AccountStatus || (AccountStatus = {}));
var AddBeneficiariesWithSafeLinkResponseError;
(function (AddBeneficiariesWithSafeLinkResponseError) {
    AddBeneficiariesWithSafeLinkResponseError["BadOrgId"] = "BAD_ORG_ID";
    AddBeneficiariesWithSafeLinkResponseError["BadQuantity"] = "BAD_QUANTITY";
    AddBeneficiariesWithSafeLinkResponseError["Unknown"] = "UNKNOWN";
})(AddBeneficiariesWithSafeLinkResponseError || (AddBeneficiariesWithSafeLinkResponseError = {}));
var AddBeneficiaryError;
(function (AddBeneficiaryError) {
    AddBeneficiaryError["AlreadyBeneficiary"] = "ALREADY_BENEFICIARY";
    AddBeneficiaryError["BirthdateFormat"] = "BIRTHDATE_FORMAT";
    AddBeneficiaryError["BirthdateFuture"] = "BIRTHDATE_FUTURE";
    AddBeneficiaryError["Unknown"] = "UNKNOWN";
    AddBeneficiaryError["UnknownLocale"] = "UNKNOWN_LOCALE";
})(AddBeneficiaryError || (AddBeneficiaryError = {}));
var AddBeneficiaryErrorV2;
(function (AddBeneficiaryErrorV2) {
    AddBeneficiaryErrorV2["NoEmailOrLogin"] = "NO_EMAIL_OR_LOGIN";
})(AddBeneficiaryErrorV2 || (AddBeneficiaryErrorV2 = {}));
var AddBeneficiaryWarning;
(function (AddBeneficiaryWarning) {
    AddBeneficiaryWarning["BeneficiaryWithDifferentLoginSameEmail"] = "BENEFICIARY_WITH_DIFFERENT_LOGIN_SAME_EMAIL";
    AddBeneficiaryWarning["BeneficiaryWithSameLoginDifferentEmail"] = "BENEFICIARY_WITH_SAME_LOGIN_DIFFERENT_EMAIL";
})(AddBeneficiaryWarning || (AddBeneficiaryWarning = {}));
var AdditionalDataRole;
(function (AdditionalDataRole) {
    AdditionalDataRole["Beneficiary"] = "BENEFICIARY";
    AdditionalDataRole["Manager"] = "MANAGER";
})(AdditionalDataRole || (AdditionalDataRole = {}));
var AddressTypeGql;
(function (AddressTypeGql) {
    AddressTypeGql["Billing"] = "BILLING";
    AddressTypeGql["Shipping"] = "SHIPPING";
})(AddressTypeGql || (AddressTypeGql = {}));
var AssociationType;
(function (AssociationType) {
    AssociationType["AssociationTypeFoodShipping"] = "ASSOCIATION_TYPE_FOOD_SHIPPING";
    AssociationType["AssociationTypeOrgaBilling"] = "ASSOCIATION_TYPE_ORGA_BILLING";
    AssociationType["AssociationTypeOrgaLocation"] = "ASSOCIATION_TYPE_ORGA_LOCATION";
    AssociationType["AssociationTypeOrgaShipping"] = "ASSOCIATION_TYPE_ORGA_SHIPPING";
    AssociationType["AssociationTypeUnspecified"] = "ASSOCIATION_TYPE_UNSPECIFIED";
    AssociationType["AssociationTypeUserBilling"] = "ASSOCIATION_TYPE_USER_BILLING";
    AssociationType["AssociationTypeUserLocation"] = "ASSOCIATION_TYPE_USER_LOCATION";
    AssociationType["AssociationTypeUserShipping"] = "ASSOCIATION_TYPE_USER_SHIPPING";
})(AssociationType || (AssociationType = {}));
var BankingCardBrand;
(function (BankingCardBrand) {
    BankingCardBrand["Mastercard"] = "MASTERCARD";
    BankingCardBrand["Unknown"] = "UNKNOWN";
    BankingCardBrand["Visa"] = "VISA";
})(BankingCardBrand || (BankingCardBrand = {}));
var BannerType;
(function (BannerType) {
    BannerType["Desktop"] = "DESKTOP";
    BannerType["Mobile"] = "MOBILE";
})(BannerType || (BannerType = {}));
var BenefitCanonicalName;
(function (BenefitCanonicalName) {
    BenefitCanonicalName["CultureCheck"] = "CULTURE_CHECK";
    BenefitCanonicalName["GiftCheck"] = "GIFT_CHECK";
    BenefitCanonicalName["Unrecognized"] = "UNRECOGNIZED";
})(BenefitCanonicalName || (BenefitCanonicalName = {}));
var CampaignStatus;
(function (CampaignStatus) {
    CampaignStatus["Cancelled"] = "CANCELLED";
    CampaignStatus["Ended"] = "ENDED";
    CampaignStatus["Planned"] = "PLANNED";
    CampaignStatus["Refund"] = "REFUND";
    CampaignStatus["Running"] = "RUNNING";
})(CampaignStatus || (CampaignStatus = {}));
var CancelReason;
(function (CancelReason) {
    CancelReason["ManagerDemand"] = "MANAGER_DEMAND";
    CancelReason["PaymentDefault"] = "PAYMENT_DEFAULT";
    CancelReason["Undefined"] = "UNDEFINED";
})(CancelReason || (CancelReason = {}));
var CardRefundReason;
(function (CardRefundReason) {
    CardRefundReason["AcceptableWithdrawalPeriod"] = "ACCEPTABLE_WITHDRAWAL_PERIOD";
    CardRefundReason["CardBlockedCode"] = "CARD_BLOCKED_CODE";
    CardRefundReason["CardDoubleCode"] = "CARD_DOUBLE_CODE";
    CardRefundReason["CardFraud"] = "CARD_FRAUD";
    CardRefundReason["CardProsodieTilloBlockedCode"] = "CARD_PROSODIE_TILLO_BLOCKED_CODE";
    CardRefundReason["CardShopTeamResponsability"] = "CARD_SHOP_TEAM_RESPONSABILITY";
    CardRefundReason["CardShopTeamValidation"] = "CARD_SHOP_TEAM_VALIDATION";
    CardRefundReason["CardVirtualTicketsSoldOut"] = "CARD_VIRTUAL_TICKETS_SOLD_OUT";
    CardRefundReason["CommercialGesture"] = "COMMERCIAL_GESTURE";
    CardRefundReason["MultiAskedByCsm"] = "MULTI_ASKED_BY_CSM";
    CardRefundReason["MultiBlocked"] = "MULTI_BLOCKED";
    CardRefundReason["MultiDifferentExpirationDate"] = "MULTI_DIFFERENT_EXPIRATION_DATE";
    CardRefundReason["MultiLost"] = "MULTI_LOST";
    CardRefundReason["MultiNotReceived"] = "MULTI_NOT_RECEIVED";
    CardRefundReason["MultiNotReceivedFullRefund"] = "MULTI_NOT_RECEIVED_FULL_REFUND";
    CardRefundReason["MultiOppositionFailed"] = "MULTI_OPPOSITION_FAILED";
    CardRefundReason["MultiOppositionMistake"] = "MULTI_OPPOSITION_MISTAKE";
    CardRefundReason["MultiStolen"] = "MULTI_STOLEN";
    CardRefundReason["MultiUnlockFailed"] = "MULTI_UNLOCK_FAILED";
    CardRefundReason["MultiUnreadable"] = "MULTI_UNREADABLE";
})(CardRefundReason || (CardRefundReason = {}));
var CardType;
(function (CardType) {
    CardType["Code"] = "CODE";
    CardType["Manual"] = "MANUAL";
    CardType["Multi"] = "MULTI";
    CardType["Physical"] = "PHYSICAL";
    CardType["PrivilegedDeal"] = "PRIVILEGED_DEAL";
    CardType["RemoteCode"] = "REMOTE_CODE";
    CardType["Unknown"] = "UNKNOWN";
    CardType["Voucher"] = "VOUCHER";
})(CardType || (CardType = {}));
var CartStatus;
(function (CartStatus) {
    CartStatus["Failure"] = "FAILURE";
    CartStatus["Pending"] = "PENDING";
    CartStatus["Success"] = "SUCCESS";
    CartStatus["Timeout"] = "TIMEOUT";
    CartStatus["Unknown"] = "UNKNOWN";
})(CartStatus || (CartStatus = {}));
var ChangePasswordError;
(function (ChangePasswordError) {
    ChangePasswordError["OldPassword"] = "OLD_PASSWORD";
    ChangePasswordError["PatternMismatch"] = "PATTERN_MISMATCH";
    ChangePasswordError["Unknown"] = "UNKNOWN";
    ChangePasswordError["WeakStrength"] = "WEAK_STRENGTH";
})(ChangePasswordError || (ChangePasswordError = {}));
var CompanyOrderType;
(function (CompanyOrderType) {
    CompanyOrderType["CompanyBill"] = "COMPANY_BILL";
    CompanyOrderType["CompanyBillPaper"] = "COMPANY_BILL_PAPER";
    CompanyOrderType["CompanyDebitNoShowRefunding"] = "COMPANY_DEBIT_NO_SHOW_REFUNDING";
    CompanyOrderType["CompanyShippingCharge"] = "COMPANY_SHIPPING_CHARGE";
    CompanyOrderType["CreditCompany"] = "CREDIT_COMPANY";
    CompanyOrderType["CreditCompanyPaper"] = "CREDIT_COMPANY_PAPER";
})(CompanyOrderType || (CompanyOrderType = {}));
var CompanySegment;
(function (CompanySegment) {
    CompanySegment["CompanySegmentCse"] = "COMPANY_SEGMENT_CSE";
    CompanySegment["CompanySegmentIncentive"] = "COMPANY_SEGMENT_INCENTIVE";
    CompanySegment["CompanySegmentRh"] = "COMPANY_SEGMENT_RH";
    CompanySegment["CompanySegmentSme"] = "COMPANY_SEGMENT_SME";
})(CompanySegment || (CompanySegment = {}));
var CreateBankAccountErrorType;
(function (CreateBankAccountErrorType) {
    CreateBankAccountErrorType["AccountNameAlreadyExist"] = "ACCOUNT_NAME_ALREADY_EXIST";
    CreateBankAccountErrorType["InvalidIban"] = "INVALID_IBAN";
    CreateBankAccountErrorType["Unknown"] = "UNKNOWN";
})(CreateBankAccountErrorType || (CreateBankAccountErrorType = {}));
var CriteriaOperator;
(function (CriteriaOperator) {
    CriteriaOperator["Contain"] = "CONTAIN";
    CriteriaOperator["DoNotWishToFill"] = "DO_NOT_WISH_TO_FILL";
    CriteriaOperator["Equals"] = "EQUALS";
    CriteriaOperator["Greater"] = "GREATER";
    CriteriaOperator["GreaterOrEqual"] = "GREATER_OR_EQUAL";
    CriteriaOperator["IntervalGreater"] = "INTERVAL_GREATER";
    CriteriaOperator["IntervalLower"] = "INTERVAL_LOWER";
    CriteriaOperator["Lower"] = "LOWER";
    CriteriaOperator["LowerOrEqual"] = "LOWER_OR_EQUAL";
    CriteriaOperator["NotContain"] = "NOT_CONTAIN";
    CriteriaOperator["NotEquals"] = "NOT_EQUALS";
    CriteriaOperator["OneOf"] = "ONE_OF";
    CriteriaOperator["Unspecified"] = "UNSPECIFIED";
})(CriteriaOperator || (CriteriaOperator = {}));
var CurrentTermPeriodType;
(function (CurrentTermPeriodType) {
    CurrentTermPeriodType["Day"] = "DAY";
    CurrentTermPeriodType["Month"] = "MONTH";
    CurrentTermPeriodType["Week"] = "WEEK";
    CurrentTermPeriodType["Year"] = "YEAR";
})(CurrentTermPeriodType || (CurrentTermPeriodType = {}));
var DetachedBeneficiaryError;
(function (DetachedBeneficiaryError) {
    DetachedBeneficiaryError["DeniedMainManager"] = "DENIED_MAIN_MANAGER";
    DetachedBeneficiaryError["DeniedSelf"] = "DENIED_SELF";
    DetachedBeneficiaryError["Unknown"] = "UNKNOWN";
})(DetachedBeneficiaryError || (DetachedBeneficiaryError = {}));
var DistributionRevertReason;
(function (DistributionRevertReason) {
    DistributionRevertReason["DistributionRevert"] = "DISTRIBUTION_REVERT";
    DistributionRevertReason["UserDeleted"] = "USER_DELETED";
})(DistributionRevertReason || (DistributionRevertReason = {}));
var EmailStatus;
(function (EmailStatus) {
    EmailStatus["Bounce"] = "BOUNCE";
    EmailStatus["Click"] = "CLICK";
    EmailStatus["Deferred"] = "DEFERRED";
    EmailStatus["Delivered"] = "DELIVERED";
    EmailStatus["Dropped"] = "DROPPED";
    EmailStatus["NotSent"] = "NOT_SENT";
    EmailStatus["Open"] = "OPEN";
    EmailStatus["Processed"] = "PROCESSED";
    EmailStatus["Sent"] = "SENT";
    EmailStatus["SpamReport"] = "SPAM_REPORT";
    EmailStatus["Unknown"] = "UNKNOWN";
})(EmailStatus || (EmailStatus = {}));
var EmailSubType;
(function (EmailSubType) {
    EmailSubType["Automatic"] = "AUTOMATIC";
    EmailSubType["Changed"] = "CHANGED";
    EmailSubType["ExternalReset"] = "EXTERNAL_RESET";
    EmailSubType["Failed"] = "FAILED";
    EmailSubType["Manual"] = "MANUAL";
    EmailSubType["NewContactAddress"] = "NEW_CONTACT_ADDRESS";
    EmailSubType["OldContactAddress"] = "OLD_CONTACT_ADDRESS";
    EmailSubType["Other"] = "OTHER";
    EmailSubType["Receiver"] = "RECEIVER";
    EmailSubType["Reset"] = "RESET";
    EmailSubType["Sender"] = "SENDER";
})(EmailSubType || (EmailSubType = {}));
var EmailType;
(function (EmailType) {
    EmailType["Burn"] = "BURN";
    EmailType["ContactMailChange"] = "CONTACT_MAIL_CHANGE";
    EmailType["Distribution"] = "DISTRIBUTION";
    EmailType["Invitation"] = "INVITATION";
    EmailType["Other"] = "OTHER";
    EmailType["Password"] = "PASSWORD";
    EmailType["PasswordChanged"] = "PASSWORD_CHANGED";
    EmailType["PasswordReset"] = "PASSWORD_RESET";
    EmailType["Relaunch"] = "RELAUNCH";
    EmailType["Share"] = "SHARE";
    EmailType["ShareCompleted"] = "SHARE_COMPLETED";
    EmailType["ShareFailed"] = "SHARE_FAILED";
})(EmailType || (EmailType = {}));
var EntityType;
(function (EntityType) {
    EntityType["GlobalEntity"] = "GLOBAL_ENTITY";
    EntityType["OrganizationEntity"] = "ORGANIZATION_ENTITY";
    EntityType["UserEntity"] = "USER_ENTITY";
})(EntityType || (EntityType = {}));
var ErrorDetail;
(function (ErrorDetail) {
    /**
     * The deadline expired before the operation could complete.
     *
     * For operations that change the state of the system, this error
     * may be returned even if the operation has completed successfully.
     * For example, a successful response from a server could have been
     * delayed long enough for the deadline to expire.
     *
     * HTTP Mapping: 504 Gateway Timeout
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["DeadlineExceeded"] = "DEADLINE_EXCEEDED";
    /**
     * The server detected that the client is exhibiting a behavior that
     * might be generating excessive load.
     *
     * HTTP Mapping: 429 Too Many Requests or 420 Enhance Your Calm
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["EnhanceYourCalm"] = "ENHANCE_YOUR_CALM";
    /**
     * The requested field is not found in the schema.
     *
     * This differs from `NOT_FOUND` in that `NOT_FOUND` should be used when a
     * query is valid, but is unable to return a result (if, for example, a
     * specific video id doesn't exist). `FIELD_NOT_FOUND` is intended to be
     * returned by the server to signify that the requested field is not known to exist.
     * This may be returned in lieu of failing the entire query.
     * See also `PERMISSION_DENIED` for cases where the
     * requested field is invalid only for the given user or class of users.
     *
     * HTTP Mapping: 404 Not Found
     * Error Type: BAD_REQUEST
     */
    ErrorDetail["FieldNotFound"] = "FIELD_NOT_FOUND";
    /**
     * The client specified an invalid argument.
     *
     * Note that this differs from `FAILED_PRECONDITION`.
     * `INVALID_ARGUMENT` indicates arguments that are problematic
     * regardless of the state of the system (e.g., a malformed file name).
     *
     * HTTP Mapping: 400 Bad Request
     * Error Type: BAD_REQUEST
     */
    ErrorDetail["InvalidArgument"] = "INVALID_ARGUMENT";
    /**
     * The provided cursor is not valid.
     *
     * The most common usage for this error is when a client is paginating
     * through a list that uses stateful cursors. In that case, the provided
     * cursor may be expired.
     *
     * HTTP Mapping: 404 Not Found
     * Error Type: NOT_FOUND
     */
    ErrorDetail["InvalidCursor"] = "INVALID_CURSOR";
    /**
     * Unable to perform operation because a required resource is missing.
     *
     * Example: Client is attempting to refresh a list, but the specified
     * list is expired. This requires an action by the client to get a new list.
     *
     * If the user is simply trying GET a resource that is not found,
     * use the NOT_FOUND error type. FAILED_PRECONDITION.MISSING_RESOURCE
     * is to be used particularly when the user is performing an operation
     * that requires a particular resource to exist.
     *
     * HTTP Mapping: 400 Bad Request or 500 Internal Server Error
     * Error Type: FAILED_PRECONDITION
     */
    ErrorDetail["MissingResource"] = "MISSING_RESOURCE";
    /**
     * Service Error.
     *
     * There is a problem with an upstream service.
     *
     * This may be returned if a gateway receives an unknown error from a service
     * or if a service is unreachable.
     * If a request times out which waiting on a response from a service,
     * `DEADLINE_EXCEEDED` may be returned instead.
     * If a service returns a more specific error Type, the specific error Type may
     * be returned instead.
     *
     * HTTP Mapping: 502 Bad Gateway
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["ServiceError"] = "SERVICE_ERROR";
    /**
     * Request failed due to network errors.
     *
     * HTTP Mapping: 503 Unavailable
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["TcpFailure"] = "TCP_FAILURE";
    /**
     * Request throttled based on server concurrency limits.
     *
     * HTTP Mapping: 503 Unavailable
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["ThrottledConcurrency"] = "THROTTLED_CONCURRENCY";
    /**
     * Request throttled based on server CPU limits
     *
     * HTTP Mapping: 503 Unavailable.
     * Error Type: UNAVAILABLE
     */
    ErrorDetail["ThrottledCpu"] = "THROTTLED_CPU";
    /**
     * The operation is not implemented or is not currently supported/enabled.
     *
     * HTTP Mapping: 501 Not Implemented
     * Error Type: BAD_REQUEST
     */
    ErrorDetail["Unimplemented"] = "UNIMPLEMENTED";
    /**
     * Unknown error.
     *
     * This error should only be returned when no other error detail applies.
     * If a client sees an unknown errorDetail, it will be interpreted as UNKNOWN.
     *
     * HTTP Mapping: 500 Internal Server Error
     */
    ErrorDetail["Unknown"] = "UNKNOWN";
})(ErrorDetail || (ErrorDetail = {}));
var ErrorType;
(function (ErrorType) {
    /**
     * Bad Request.
     *
     * There is a problem with the request.
     * Retrying the same request is not likely to succeed.
     * An example would be a query or argument that cannot be deserialized.
     *
     * HTTP Mapping: 400 Bad Request
     */
    ErrorType["BadRequest"] = "BAD_REQUEST";
    /**
     * The operation was rejected because the system is not in a state
     * required for the operation's execution.  For example, the directory
     * to be deleted is non-empty, an rmdir operation is applied to
     * a non-directory, etc.
     *
     * Service implementers can use the following guidelines to decide
     * between `FAILED_PRECONDITION` and `UNAVAILABLE`:
     *
     * - Use `UNAVAILABLE` if the client can retry just the failing call.
     * - Use `FAILED_PRECONDITION` if the client should not retry until
     * the system state has been explicitly fixed.  E.g., if an "rmdir"
     *      fails because the directory is non-empty, `FAILED_PRECONDITION`
     * should be returned since the client should not retry unless
     * the files are deleted from the directory.
     *
     * HTTP Mapping: 400 Bad Request or 500 Internal Server Error
     */
    ErrorType["FailedPrecondition"] = "FAILED_PRECONDITION";
    /**
     * Internal error.
     *
     * An unexpected internal error was encountered. This means that some
     * invariants expected by the underlying system have been broken.
     * This error code is reserved for serious errors.
     *
     * HTTP Mapping: 500 Internal Server Error
     */
    ErrorType["Internal"] = "INTERNAL";
    /**
     * The requested entity was not found.
     *
     * This could apply to a resource that has never existed (e.g. bad resource id),
     * or a resource that no longer exists (e.g. cache expired.)
     *
     * Note to server developers: if a request is denied for an entire class
     * of users, such as gradual feature rollout or undocumented allowlist,
     * `NOT_FOUND` may be used. If a request is denied for some users within
     * a class of users, such as user-based access control, `PERMISSION_DENIED`
     * must be used.
     *
     * HTTP Mapping: 404 Not Found
     */
    ErrorType["NotFound"] = "NOT_FOUND";
    /**
     * The caller does not have permission to execute the specified
     * operation.
     *
     * `PERMISSION_DENIED` must not be used for rejections
     * caused by exhausting some resource or quota.
     * `PERMISSION_DENIED` must not be used if the caller
     * cannot be identified (use `UNAUTHENTICATED`
     * instead for those errors).
     *
     * This error Type does not imply the
     * request is valid or the requested entity exists or satisfies
     * other pre-conditions.
     *
     * HTTP Mapping: 403 Forbidden
     */
    ErrorType["PermissionDenied"] = "PERMISSION_DENIED";
    /**
     * The request does not have valid authentication credentials.
     *
     * This is intended to be returned only for routes that require
     * authentication.
     *
     * HTTP Mapping: 401 Unauthorized
     */
    ErrorType["Unauthenticated"] = "UNAUTHENTICATED";
    /**
     * Currently Unavailable.
     *
     * The service is currently unavailable.  This is most likely a
     * transient condition, which can be corrected by retrying with
     * a backoff.
     *
     * HTTP Mapping: 503 Unavailable
     */
    ErrorType["Unavailable"] = "UNAVAILABLE";
    /**
     * Unknown error.
     *
     * For example, this error may be returned when
     * an error code received from another address space belongs to
     * an error space that is not known in this address space.  Also
     * errors raised by APIs that do not return enough error information
     * may be converted to this error.
     *
     * If a client sees an unknown errorType, it will be interpreted as UNKNOWN.
     * Unknown errors MUST NOT trigger any special behavior. These MAY be treated
     * by an implementation as being equivalent to INTERNAL.
     *
     * When possible, a more specific error should be provided.
     *
     * HTTP Mapping: 520 Unknown Error
     */
    ErrorType["Unknown"] = "UNKNOWN";
})(ErrorType || (ErrorType = {}));
var FileFormat;
(function (FileFormat) {
    FileFormat["Excel"] = "EXCEL";
    FileFormat["Pdf"] = "PDF";
})(FileFormat || (FileFormat = {}));
var FundedShippingMode;
(function (FundedShippingMode) {
    FundedShippingMode["Company"] = "COMPANY";
    FundedShippingMode["Consumer"] = "CONSUMER";
})(FundedShippingMode || (FundedShippingMode = {}));
var GiftTaskStatus;
(function (GiftTaskStatus) {
    GiftTaskStatus["Completed"] = "COMPLETED";
    GiftTaskStatus["Executing"] = "EXECUTING";
    GiftTaskStatus["Failed"] = "FAILED";
    GiftTaskStatus["Finished"] = "FINISHED";
    GiftTaskStatus["Rejected"] = "REJECTED";
    GiftTaskStatus["Starting"] = "STARTING";
})(GiftTaskStatus || (GiftTaskStatus = {}));
var GiftTaskType;
(function (GiftTaskType) {
    GiftTaskType["AnonymousBeneficiariesCreation"] = "ANONYMOUS_BENEFICIARIES_CREATION";
    GiftTaskType["BeneficiariesArchival"] = "BENEFICIARIES_ARCHIVAL";
    GiftTaskType["BeneficiariesCreation"] = "BENEFICIARIES_CREATION";
    GiftTaskType["CampaignsReport"] = "CAMPAIGNS_REPORT";
    GiftTaskType["CampaignCreation"] = "CAMPAIGN_CREATION";
    GiftTaskType["CardsCodesEdition"] = "CARDS_CODES_EDITION";
    GiftTaskType["CardsImport"] = "CARDS_IMPORT";
    GiftTaskType["CardsPricesEdition"] = "CARDS_PRICES_EDITION";
    GiftTaskType["CardExpedition"] = "CARD_EXPEDITION";
    GiftTaskType["CardExport"] = "CARD_EXPORT";
    GiftTaskType["CompanyBeneficiariesAccessExportGeneration"] = "COMPANY_BENEFICIARIES_ACCESS_EXPORT_GENERATION";
    GiftTaskType["CompanyBeneficiariesExportGeneration"] = "COMPANY_BENEFICIARIES_EXPORT_GENERATION";
    GiftTaskType["CompanyBeneficiaryAccessUpdate"] = "COMPANY_BENEFICIARY_ACCESS_UPDATE";
    GiftTaskType["CompanyCategoryUpdate"] = "COMPANY_CATEGORY_UPDATE";
    GiftTaskType["CompanyManagerUpdate"] = "COMPANY_MANAGER_UPDATE";
    GiftTaskType["ConsumedPrimaryCodesExport"] = "CONSUMED_PRIMARY_CODES_EXPORT";
    GiftTaskType["CreateMultiShopCards"] = "CREATE_MULTI_SHOP_CARDS";
    GiftTaskType["DistributionsCancel"] = "DISTRIBUTIONS_CANCEL";
    GiftTaskType["GenerateCardVisuals"] = "GENERATE_CARD_VISUALS";
    GiftTaskType["GroupRefund"] = "GROUP_REFUND";
    GiftTaskType["ManualJob"] = "MANUAL_JOB";
    GiftTaskType["OrderCsvAnalyze"] = "ORDER_CSV_ANALYZE";
    GiftTaskType["OrderValidation"] = "ORDER_VALIDATION";
    GiftTaskType["Sharing"] = "SHARING";
    GiftTaskType["StockAlertImport"] = "STOCK_ALERT_IMPORT";
    GiftTaskType["UsersSync"] = "USERS_SYNC";
    GiftTaskType["UserDelete"] = "USER_DELETE";
    GiftTaskType["UserInvite"] = "USER_INVITE";
    GiftTaskType["UserLoginRename"] = "USER_LOGIN_RENAME";
    GiftTaskType["UserRelaunch"] = "USER_RELAUNCH";
    GiftTaskType["UserRelaunchAutomatic"] = "USER_RELAUNCH_AUTOMATIC";
    GiftTaskType["WalletSpecificSettingsImport"] = "WALLET_SPECIFIC_SETTINGS_IMPORT";
})(GiftTaskType || (GiftTaskType = {}));
var IdentifierType;
(function (IdentifierType) {
    IdentifierType["EuropeanVatNumber"] = "EUROPEAN_VAT_NUMBER";
    IdentifierType["Rna"] = "RNA";
    IdentifierType["Siret"] = "SIRET";
})(IdentifierType || (IdentifierType = {}));
var InvitationType;
(function (InvitationType) {
    InvitationType["Site"] = "SITE";
    InvitationType["Standard"] = "STANDARD";
})(InvitationType || (InvitationType = {}));
var Language;
(function (Language) {
    Language["Dutch"] = "DUTCH";
    Language["English"] = "ENGLISH";
    Language["French"] = "FRENCH";
})(Language || (Language = {}));
var MailTrackingStatus;
(function (MailTrackingStatus) {
    MailTrackingStatus["Bounce"] = "BOUNCE";
    MailTrackingStatus["Click"] = "CLICK";
    MailTrackingStatus["Deferred"] = "DEFERRED";
    MailTrackingStatus["Delivered"] = "DELIVERED";
    MailTrackingStatus["Dropped"] = "DROPPED";
    MailTrackingStatus["NotSent"] = "NOT_SENT";
    MailTrackingStatus["Open"] = "OPEN";
    MailTrackingStatus["Processed"] = "PROCESSED";
    MailTrackingStatus["Sent"] = "SENT";
    MailTrackingStatus["SpamReport"] = "SPAM_REPORT";
    MailTrackingStatus["Unknown"] = "UNKNOWN";
})(MailTrackingStatus || (MailTrackingStatus = {}));
var MassFileUpdateType;
(function (MassFileUpdateType) {
    MassFileUpdateType["MassKamUpdate"] = "MASS_KAM_UPDATE";
    MassFileUpdateType["MassOrgInfoUpdate"] = "MASS_ORG_INFO_UPDATE";
})(MassFileUpdateType || (MassFileUpdateType = {}));
var MessageType;
(function (MessageType) {
    MessageType["Free"] = "FREE";
    MessageType["Standard"] = "STANDARD";
})(MessageType || (MessageType = {}));
var MigrationOrigin;
(function (MigrationOrigin) {
    MigrationOrigin["Glady"] = "GLADY";
    MigrationOrigin["Sodexo"] = "SODEXO";
})(MigrationOrigin || (MigrationOrigin = {}));
var OrderMode;
(function (OrderMode) {
    OrderMode["Postpaid"] = "POSTPAID";
    OrderMode["Prepaid"] = "PREPAID";
    OrderMode["Unknown"] = "UNKNOWN";
})(OrderMode || (OrderMode = {}));
var OrderStatus;
(function (OrderStatus) {
    OrderStatus["Created"] = "CREATED";
    OrderStatus["Paid"] = "PAID";
    OrderStatus["Sent"] = "SENT";
    OrderStatus["Validated"] = "VALIDATED";
    OrderStatus["WaitingForPayment"] = "WAITING_FOR_PAYMENT";
})(OrderStatus || (OrderStatus = {}));
var OrganizationCountry;
(function (OrganizationCountry) {
    OrganizationCountry["Belgium"] = "BELGIUM";
    OrganizationCountry["France"] = "FRANCE";
    OrganizationCountry["Unknown"] = "UNKNOWN";
})(OrganizationCountry || (OrganizationCountry = {}));
var PacAdditionalDataStatus;
(function (PacAdditionalDataStatus) {
    PacAdditionalDataStatus["AcceptedByManager"] = "ACCEPTED_BY_MANAGER";
    PacAdditionalDataStatus["FilledByBenef"] = "FILLED_BY_BENEF";
    PacAdditionalDataStatus["FilledByManager"] = "FILLED_BY_MANAGER";
    PacAdditionalDataStatus["RefusedByBenef"] = "REFUSED_BY_BENEF";
})(PacAdditionalDataStatus || (PacAdditionalDataStatus = {}));
var PacBatchJobStatus;
(function (PacBatchJobStatus) {
    PacBatchJobStatus["Error"] = "ERROR";
    PacBatchJobStatus["InProgress"] = "IN_PROGRESS";
    PacBatchJobStatus["New"] = "NEW";
    PacBatchJobStatus["Success"] = "SUCCESS";
    PacBatchJobStatus["Unspecified"] = "UNSPECIFIED";
})(PacBatchJobStatus || (PacBatchJobStatus = {}));
var PacBatchJobType;
(function (PacBatchJobType) {
    PacBatchJobType["Ancv"] = "ANCV";
    PacBatchJobType["Distrib"] = "DISTRIB";
    PacBatchJobType["ExportAdditionalData"] = "EXPORT_ADDITIONAL_DATA";
    PacBatchJobType["ExportRightOwner"] = "EXPORT_RIGHT_OWNER";
    PacBatchJobType["Sepa"] = "SEPA";
    PacBatchJobType["Unspecified"] = "UNSPECIFIED";
})(PacBatchJobType || (PacBatchJobType = {}));
var PacCriteriaOperator;
(function (PacCriteriaOperator) {
    PacCriteriaOperator["Contain"] = "CONTAIN";
    PacCriteriaOperator["DoNotWishToFill"] = "DO_NOT_WISH_TO_FILL";
    PacCriteriaOperator["Equals"] = "EQUALS";
    PacCriteriaOperator["Greater"] = "GREATER";
    PacCriteriaOperator["GreaterOrEqual"] = "GREATER_OR_EQUAL";
    PacCriteriaOperator["IntervalGreater"] = "INTERVAL_GREATER";
    PacCriteriaOperator["IntervalLower"] = "INTERVAL_LOWER";
    PacCriteriaOperator["Lower"] = "LOWER";
    PacCriteriaOperator["LowerOrEqual"] = "LOWER_OR_EQUAL";
    PacCriteriaOperator["NotContain"] = "NOT_CONTAIN";
    PacCriteriaOperator["NotEquals"] = "NOT_EQUALS";
    PacCriteriaOperator["OneOf"] = "ONE_OF";
    PacCriteriaOperator["Unrecognized"] = "UNRECOGNIZED";
    PacCriteriaOperator["Unspecified"] = "UNSPECIFIED";
})(PacCriteriaOperator || (PacCriteriaOperator = {}));
var PacEpCriteriaOperator;
(function (PacEpCriteriaOperator) {
    PacEpCriteriaOperator["Contain"] = "CONTAIN";
    PacEpCriteriaOperator["DoNotWishToFill"] = "DO_NOT_WISH_TO_FILL";
    PacEpCriteriaOperator["Equals"] = "EQUALS";
    PacEpCriteriaOperator["Greater"] = "GREATER";
    PacEpCriteriaOperator["GreaterOrEqual"] = "GREATER_OR_EQUAL";
    PacEpCriteriaOperator["IntervalGreater"] = "INTERVAL_GREATER";
    PacEpCriteriaOperator["IntervalLower"] = "INTERVAL_LOWER";
    PacEpCriteriaOperator["Lower"] = "LOWER";
    PacEpCriteriaOperator["LowerOrEqual"] = "LOWER_OR_EQUAL";
    PacEpCriteriaOperator["NotContain"] = "NOT_CONTAIN";
    PacEpCriteriaOperator["NotEquals"] = "NOT_EQUALS";
    PacEpCriteriaOperator["OneOf"] = "ONE_OF";
    PacEpCriteriaOperator["Unrecognized"] = "UNRECOGNIZED";
    PacEpCriteriaOperator["Unspecified"] = "UNSPECIFIED";
})(PacEpCriteriaOperator || (PacEpCriteriaOperator = {}));
var PacEpExportType;
(function (PacEpExportType) {
    PacEpExportType["Children"] = "CHILDREN";
    PacEpExportType["Schemas"] = "SCHEMAS";
})(PacEpExportType || (PacEpExportType = {}));
var PacEpRightsOwnerField;
(function (PacEpRightsOwnerField) {
    PacEpRightsOwnerField["FieldBirthdate"] = "FIELD_BIRTHDATE";
    PacEpRightsOwnerField["FieldRelationtype"] = "FIELD_RELATIONTYPE";
    PacEpRightsOwnerField["FieldUniondate"] = "FIELD_UNIONDATE";
    PacEpRightsOwnerField["FieldUnspecified"] = "FIELD_UNSPECIFIED";
    PacEpRightsOwnerField["Unrecognized"] = "UNRECOGNIZED";
})(PacEpRightsOwnerField || (PacEpRightsOwnerField = {}));
var PacEpTarget;
(function (PacEpTarget) {
    PacEpTarget["Beneficiary"] = "BENEFICIARY";
    PacEpTarget["RightOwner"] = "RIGHT_OWNER";
})(PacEpTarget || (PacEpTarget = {}));
var PacOrderStatus;
(function (PacOrderStatus) {
    PacOrderStatus["Canceled"] = "CANCELED";
    PacOrderStatus["Paid"] = "PAID";
    PacOrderStatus["Pending"] = "PENDING";
    PacOrderStatus["Refunded"] = "REFUNDED";
})(PacOrderStatus || (PacOrderStatus = {}));
var PacPerkAncvDeliveryPoint;
(function (PacPerkAncvDeliveryPoint) {
    PacPerkAncvDeliveryPoint["AncvDeliveryPointDomicile"] = "ANCV_DELIVERY_POINT_DOMICILE";
    PacPerkAncvDeliveryPoint["AncvDeliveryPointHeadquarters"] = "ANCV_DELIVERY_POINT_HEADQUARTERS";
})(PacPerkAncvDeliveryPoint || (PacPerkAncvDeliveryPoint = {}));
var PacPerkAncvShipmentType;
(function (PacPerkAncvShipmentType) {
    PacPerkAncvShipmentType["AncvShipmentTypeCourrierSimple"] = "ANCV_SHIPMENT_TYPE_COURRIER_SIMPLE";
    PacPerkAncvShipmentType["AncvShipmentTypeLettreSuivie"] = "ANCV_SHIPMENT_TYPE_LETTRE_SUIVIE";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeArR1"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_AR_R1";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeArR2"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_AR_R2";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeArR3"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_AR_R3";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeSimpleR1"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_SIMPLE_R1";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeSimpleR2"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_SIMPLE_R2";
    PacPerkAncvShipmentType["AncvShipmentTypeRecommandeSimpleR3"] = "ANCV_SHIPMENT_TYPE_RECOMMANDE_SIMPLE_R3";
})(PacPerkAncvShipmentType || (PacPerkAncvShipmentType = {}));
var PacPerkAncvType;
(function (PacPerkAncvType) {
    PacPerkAncvType["Both"] = "BOTH";
    PacPerkAncvType["Connect"] = "CONNECT";
    PacPerkAncvType["Paper"] = "PAPER";
})(PacPerkAncvType || (PacPerkAncvType = {}));
var PacPerkStatus;
(function (PacPerkStatus) {
    PacPerkStatus["Active"] = "ACTIVE";
    PacPerkStatus["Archived"] = "ARCHIVED";
    PacPerkStatus["Draft"] = "DRAFT";
    PacPerkStatus["Hidden"] = "HIDDEN";
})(PacPerkStatus || (PacPerkStatus = {}));
var PacPerkTarget;
(function (PacPerkTarget) {
    PacPerkTarget["Child"] = "CHILD";
    PacPerkTarget["Home"] = "HOME";
})(PacPerkTarget || (PacPerkTarget = {}));
var PacPerkType;
(function (PacPerkType) {
    PacPerkType["Ancv"] = "ANCV";
    PacPerkType["CultureEndowment"] = "CULTURE_ENDOWMENT";
    PacPerkType["Event"] = "EVENT";
    PacPerkType["GiftEndowment"] = "GIFT_ENDOWMENT";
    PacPerkType["Other"] = "OTHER";
    PacPerkType["Reimbursement"] = "REIMBURSEMENT";
    PacPerkType["Ticketing"] = "TICKETING";
    PacPerkType["TravelEndowment"] = "TRAVEL_ENDOWMENT";
})(PacPerkType || (PacPerkType = {}));
var PacPerkUpdateStatus;
(function (PacPerkUpdateStatus) {
    PacPerkUpdateStatus["StatusActive"] = "STATUS_ACTIVE";
    PacPerkUpdateStatus["StatusArchive"] = "STATUS_ARCHIVE";
    PacPerkUpdateStatus["StatusHidden"] = "STATUS_HIDDEN";
    PacPerkUpdateStatus["StatusUnspecified"] = "STATUS_UNSPECIFIED";
})(PacPerkUpdateStatus || (PacPerkUpdateStatus = {}));
var PacPerkUtilisationAncvPaperDomicileAddressCountry;
(function (PacPerkUtilisationAncvPaperDomicileAddressCountry) {
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Allemagne"] = "ALLEMAGNE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Andorre"] = "ANDORRE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Belgique"] = "BELGIQUE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Espagne"] = "ESPAGNE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["France"] = "FRANCE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Italie"] = "ITALIE";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Luxembourg"] = "LUXEMBOURG";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Monaco"] = "MONACO";
    PacPerkUtilisationAncvPaperDomicileAddressCountry["Suisse"] = "SUISSE";
})(PacPerkUtilisationAncvPaperDomicileAddressCountry || (PacPerkUtilisationAncvPaperDomicileAddressCountry = {}));
var PacPerkUtilisationStatus;
(function (PacPerkUtilisationStatus) {
    PacPerkUtilisationStatus["Canceled"] = "CANCELED";
    PacPerkUtilisationStatus["New"] = "NEW";
    PacPerkUtilisationStatus["Processed"] = "PROCESSED";
    PacPerkUtilisationStatus["Rejected"] = "REJECTED";
    PacPerkUtilisationStatus["Unspecified"] = "UNSPECIFIED";
    PacPerkUtilisationStatus["Validated"] = "VALIDATED";
})(PacPerkUtilisationStatus || (PacPerkUtilisationStatus = {}));
var PacReimbursementType;
(function (PacReimbursementType) {
    PacReimbursementType["Amount"] = "AMOUNT";
    PacReimbursementType["Percentage"] = "PERCENTAGE";
    PacReimbursementType["Unrecognized"] = "UNRECOGNIZED";
    PacReimbursementType["Unspecified"] = "UNSPECIFIED";
})(PacReimbursementType || (PacReimbursementType = {}));
var PacRelationshipType;
(function (PacRelationshipType) {
    PacRelationshipType["Marriage"] = "MARRIAGE";
    PacRelationshipType["Pacs"] = "PACS";
    PacRelationshipType["Unknown"] = "UNKNOWN";
})(PacRelationshipType || (PacRelationshipType = {}));
var PacRequestRecordStatus;
(function (PacRequestRecordStatus) {
    PacRequestRecordStatus["Accepted"] = "ACCEPTED";
    PacRequestRecordStatus["Rejected"] = "REJECTED";
    PacRequestRecordStatus["ToCheck"] = "TO_CHECK";
})(PacRequestRecordStatus || (PacRequestRecordStatus = {}));
var PacRequestStatus;
(function (PacRequestStatus) {
    PacRequestStatus["Done"] = "DONE";
    PacRequestStatus["InProgress"] = "IN_PROGRESS";
})(PacRequestStatus || (PacRequestStatus = {}));
var PacRequestType;
(function (PacRequestType) {
    PacRequestType["AdditionalData"] = "ADDITIONAL_DATA";
    PacRequestType["RoChildren"] = "RO_CHILDREN";
    PacRequestType["RoPartner"] = "RO_PARTNER";
    PacRequestType["Unknown"] = "UNKNOWN";
})(PacRequestType || (PacRequestType = {}));
var PacRightsOwnerField;
(function (PacRightsOwnerField) {
    PacRightsOwnerField["FieldBirthdate"] = "FIELD_BIRTHDATE";
    PacRightsOwnerField["FieldRelationtype"] = "FIELD_RELATIONTYPE";
    PacRightsOwnerField["FieldUniondate"] = "FIELD_UNIONDATE";
    PacRightsOwnerField["FieldUnspecified"] = "FIELD_UNSPECIFIED";
    PacRightsOwnerField["Unrecognized"] = "UNRECOGNIZED";
})(PacRightsOwnerField || (PacRightsOwnerField = {}));
var PacRightsOwnerType;
(function (PacRightsOwnerType) {
    PacRightsOwnerType["Child"] = "CHILD";
    PacRightsOwnerType["Partner"] = "PARTNER";
    PacRightsOwnerType["Unknown"] = "UNKNOWN";
})(PacRightsOwnerType || (PacRightsOwnerType = {}));
var PacRightsOwnerUnionType;
(function (PacRightsOwnerUnionType) {
    PacRightsOwnerUnionType["Marriage"] = "MARRIAGE";
    PacRightsOwnerUnionType["Pacs"] = "PACS";
})(PacRightsOwnerUnionType || (PacRightsOwnerUnionType = {}));
var PacValidationStatus;
(function (PacValidationStatus) {
    PacValidationStatus["ValidationStatusAccepted"] = "VALIDATION_STATUS_ACCEPTED";
    PacValidationStatus["ValidationStatusRejected"] = "VALIDATION_STATUS_REJECTED";
})(PacValidationStatus || (PacValidationStatus = {}));
var PaperOrderChequeClassification;
(function (PaperOrderChequeClassification) {
    PaperOrderChequeClassification["Culture"] = "CULTURE";
    PaperOrderChequeClassification["LibertyAsc"] = "LIBERTY_ASC";
    PaperOrderChequeClassification["LibertyIct"] = "LIBERTY_ICT";
})(PaperOrderChequeClassification || (PaperOrderChequeClassification = {}));
var PaperOrderChequePackaging;
(function (PaperOrderChequePackaging) {
    PaperOrderChequePackaging["Bulk"] = "BULK";
    PaperOrderChequePackaging["Checkbook"] = "CHECKBOOK";
})(PaperOrderChequePackaging || (PaperOrderChequePackaging = {}));
var PaperOrderChequeType;
(function (PaperOrderChequeType) {
    PaperOrderChequeType["AdultsChristmas"] = "ADULTS_CHRISTMAS";
    PaperOrderChequeType["BackToSchool"] = "BACK_TO_SCHOOL";
    PaperOrderChequeType["ChildrenChristmas"] = "CHILDREN_CHRISTMAS";
    PaperOrderChequeType["ChildBirth"] = "CHILD_BIRTH";
    PaperOrderChequeType["FathersDay"] = "FATHERS_DAY";
    PaperOrderChequeType["MothersDay"] = "MOTHERS_DAY";
    PaperOrderChequeType["Other"] = "OTHER";
    PaperOrderChequeType["Retirement"] = "RETIREMENT";
    PaperOrderChequeType["SaintCatherine"] = "SAINT_CATHERINE";
    PaperOrderChequeType["SaintNicolas"] = "SAINT_NICOLAS";
    PaperOrderChequeType["Weddings"] = "WEDDINGS";
})(PaperOrderChequeType || (PaperOrderChequeType = {}));
var PaperOrderOrderStatus;
(function (PaperOrderOrderStatus) {
    PaperOrderOrderStatus["Created"] = "CREATED";
    PaperOrderOrderStatus["Error"] = "ERROR";
    PaperOrderOrderStatus["Paid"] = "PAID";
    PaperOrderOrderStatus["Sent"] = "SENT";
    PaperOrderOrderStatus["WaitingForPayment"] = "WAITING_FOR_PAYMENT";
})(PaperOrderOrderStatus || (PaperOrderOrderStatus = {}));
var PaperOrderWalletType;
(function (PaperOrderWalletType) {
    PaperOrderWalletType["CultureCard"] = "CULTURE_CARD";
    PaperOrderWalletType["GiftCard"] = "GIFT_CARD";
})(PaperOrderWalletType || (PaperOrderWalletType = {}));
var PaymentFailure;
(function (PaymentFailure) {
    PaymentFailure["AmountMismatch"] = "AMOUNT_MISMATCH";
    PaymentFailure["Canceled"] = "CANCELED";
    PaymentFailure["CardCountryDeclined"] = "CARD_COUNTRY_DECLINED";
    PaymentFailure["CardDeclined"] = "CARD_DECLINED";
    PaymentFailure["CurrencyMismatch"] = "CURRENCY_MISMATCH";
    PaymentFailure["FraudSuspected"] = "FRAUD_SUSPECTED";
    PaymentFailure["IncorrectNumber"] = "INCORRECT_NUMBER";
    PaymentFailure["InsufficientFunds"] = "INSUFFICIENT_FUNDS";
    PaymentFailure["ProcessingError"] = "PROCESSING_ERROR";
    PaymentFailure["ThreeDsDeclined"] = "THREE_DS_DECLINED";
    PaymentFailure["Timeout"] = "TIMEOUT";
    PaymentFailure["Unknown"] = "UNKNOWN";
})(PaymentFailure || (PaymentFailure = {}));
var PaymentProvider;
(function (PaymentProvider) {
    PaymentProvider["Checkout"] = "CHECKOUT";
    PaymentProvider["Payplug"] = "PAYPLUG";
})(PaymentProvider || (PaymentProvider = {}));
var PaymentStatus;
(function (PaymentStatus) {
    PaymentStatus["Failed"] = "FAILED";
    PaymentStatus["Pending"] = "PENDING";
    PaymentStatus["Refunded"] = "REFUNDED";
    PaymentStatus["Success"] = "SUCCESS";
    PaymentStatus["ToBeRefunded"] = "TO_BE_REFUNDED";
    PaymentStatus["Used"] = "USED";
})(PaymentStatus || (PaymentStatus = {}));
var PaymentTargetType;
(function (PaymentTargetType) {
    PaymentTargetType["BeneficiaryPayment"] = "BENEFICIARY_PAYMENT";
    PaymentTargetType["CompanyPayment"] = "COMPANY_PAYMENT";
    PaymentTargetType["Unknown"] = "UNKNOWN";
})(PaymentTargetType || (PaymentTargetType = {}));
var PaymentType;
(function (PaymentType) {
    PaymentType["CompanyBankTransfer"] = "COMPANY_BANK_TRANSFER";
    PaymentType["NoNeed"] = "NO_NEED";
    PaymentType["UserCreditCard"] = "USER_CREDIT_CARD";
    PaymentType["UserWedoogiftCard"] = "USER_WEDOOGIFT_CARD";
})(PaymentType || (PaymentType = {}));
var ProspectFunctionEnum;
(function (ProspectFunctionEnum) {
    ProspectFunctionEnum["Direction"] = "DIRECTION";
    ProspectFunctionEnum["Drh"] = "DRH";
    ProspectFunctionEnum["MarketingCommunicationDepartment"] = "MARKETING_COMMUNICATION_DEPARTMENT";
    ProspectFunctionEnum["MemberCse"] = "MEMBER_CSE";
    ProspectFunctionEnum["OfficeManager"] = "OFFICE_MANAGER";
    ProspectFunctionEnum["Other"] = "OTHER";
    ProspectFunctionEnum["PayrollAdministrator"] = "PAYROLL_ADMINISTRATOR";
    ProspectFunctionEnum["Permanent"] = "PERMANENT";
    ProspectFunctionEnum["PurchaseDepartment"] = "PURCHASE_DEPARTMENT";
    ProspectFunctionEnum["ResponsableAssistRh"] = "RESPONSABLE_ASSIST_RH";
    ProspectFunctionEnum["ResponsableCse"] = "RESPONSABLE_CSE";
    ProspectFunctionEnum["Rh"] = "RH";
    ProspectFunctionEnum["SalesDepartment"] = "SALES_DEPARTMENT";
    ProspectFunctionEnum["TreasurerCse"] = "TREASURER_CSE";
    ProspectFunctionEnum["Unknown"] = "UNKNOWN";
})(ProspectFunctionEnum || (ProspectFunctionEnum = {}));
var ProspectStatusEnum;
(function (ProspectStatusEnum) {
    ProspectStatusEnum["Created"] = "CREATED";
    ProspectStatusEnum["Declined"] = "DECLINED";
    ProspectStatusEnum["Deleted"] = "DELETED";
    ProspectStatusEnum["KybValidationOngoing"] = "KYB_VALIDATION_ONGOING";
    ProspectStatusEnum["NeedKyb"] = "NEED_KYB";
    ProspectStatusEnum["NeedManualValidation"] = "NEED_MANUAL_VALIDATION";
    ProspectStatusEnum["NeedRecontact"] = "NEED_RECONTACT";
    ProspectStatusEnum["Ongoing"] = "ONGOING";
    ProspectStatusEnum["Unknown"] = "UNKNOWN";
    ProspectStatusEnum["Validated"] = "VALIDATED";
})(ProspectStatusEnum || (ProspectStatusEnum = {}));
var RedirectUrlType;
(function (RedirectUrlType) {
    RedirectUrlType["Absolute"] = "ABSOLUTE";
    RedirectUrlType["Inqom"] = "INQOM";
    RedirectUrlType["Relative"] = "RELATIVE";
    RedirectUrlType["Wedoofood"] = "WEDOOFOOD";
    RedirectUrlType["Wedoosite"] = "WEDOOSITE";
})(RedirectUrlType || (RedirectUrlType = {}));
var RightsOwnerField;
(function (RightsOwnerField) {
    RightsOwnerField["FieldBirthdate"] = "FIELD_BIRTHDATE";
    RightsOwnerField["FieldRelationtype"] = "FIELD_RELATIONTYPE";
    RightsOwnerField["FieldUniondate"] = "FIELD_UNIONDATE";
    RightsOwnerField["FieldUnspecified"] = "FIELD_UNSPECIFIED";
})(RightsOwnerField || (RightsOwnerField = {}));
var SearchStatus;
(function (SearchStatus) {
    SearchStatus["Active"] = "ACTIVE";
    SearchStatus["Inactive"] = "INACTIVE";
})(SearchStatus || (SearchStatus = {}));
var SearchType;
(function (SearchType) {
    SearchType["Ceil"] = "CEIL";
    SearchType["Equal"] = "EQUAL";
    SearchType["Floor"] = "FLOOR";
})(SearchType || (SearchType = {}));
var ShippingMode;
(function (ShippingMode) {
    ShippingMode["Acknowledged"] = "ACKNOWLEDGED";
    ShippingMode["Email"] = "EMAIL";
    ShippingMode["NormalLetter"] = "NORMAL_LETTER";
    ShippingMode["NormalLetterForMulti"] = "NORMAL_LETTER_FOR_MULTI";
    ShippingMode["Registered"] = "REGISTERED";
    ShippingMode["Tracked"] = "TRACKED";
})(ShippingMode || (ShippingMode = {}));
var ShopCharacteristic;
(function (ShopCharacteristic) {
    ShopCharacteristic["AllAmounts"] = "ALL_AMOUNTS";
    ShopCharacteristic["AvailableInstantly"] = "AVAILABLE_INSTANTLY";
    ShopCharacteristic["AvailableInternet"] = "AVAILABLE_INTERNET";
    ShopCharacteristic["AvailableTwoDays"] = "AVAILABLE_TWO_DAYS";
    ShopCharacteristic["CumulativeOffer"] = "CUMULATIVE_OFFER";
    ShopCharacteristic["InShopPrinted"] = "IN_SHOP_PRINTED";
    ShopCharacteristic["InShopPrintedSmartphone"] = "IN_SHOP_PRINTED_SMARTPHONE";
    ShopCharacteristic["NotAvailableInternet"] = "NOT_AVAILABLE_INTERNET";
    ShopCharacteristic["PremiumOffer"] = "PREMIUM_OFFER";
    ShopCharacteristic["PrintRequired"] = "PRINT_REQUIRED";
    ShopCharacteristic["PrivateSpace"] = "PRIVATE_SPACE";
    ShopCharacteristic["ShippedFiveDays"] = "SHIPPED_FIVE_DAYS";
    ShopCharacteristic["ShippedFourDays"] = "SHIPPED_FOUR_DAYS";
    ShopCharacteristic["ShippedSevenDays"] = "SHIPPED_SEVEN_DAYS";
    ShopCharacteristic["ShippedTenDays"] = "SHIPPED_TEN_DAYS";
    ShopCharacteristic["ShippedThreeDays"] = "SHIPPED_THREE_DAYS";
    ShopCharacteristic["Unknown"] = "UNKNOWN";
})(ShopCharacteristic || (ShopCharacteristic = {}));
var ShopCombinationType;
(function (ShopCombinationType) {
    ShopCombinationType["Combinable"] = "COMBINABLE";
    ShopCombinationType["NotCombinable"] = "NOT_COMBINABLE";
    ShopCombinationType["NoStock"] = "NO_STOCK";
    ShopCombinationType["RestrictedAmounts"] = "RESTRICTED_AMOUNTS";
    ShopCombinationType["StockAndRestricted"] = "STOCK_AND_RESTRICTED";
    ShopCombinationType["StockAndVariable"] = "STOCK_AND_VARIABLE";
    ShopCombinationType["Variable"] = "VARIABLE";
})(ShopCombinationType || (ShopCombinationType = {}));
var ShopConditionsFilter;
(function (ShopConditionsFilter) {
    ShopConditionsFilter["CartSummary"] = "CART_SUMMARY";
    ShopConditionsFilter["ConditionMail"] = "CONDITION_MAIL";
    ShopConditionsFilter["ConditionValidation"] = "CONDITION_VALIDATION";
    ShopConditionsFilter["Full"] = "FULL";
    ShopConditionsFilter["OfferwallFilter"] = "OFFERWALL_FILTER";
    ShopConditionsFilter["Shop"] = "SHOP";
})(ShopConditionsFilter || (ShopConditionsFilter = {}));
var ShopOrderingPeriodicity;
(function (ShopOrderingPeriodicity) {
    ShopOrderingPeriodicity["Monthly"] = "MONTHLY";
    ShopOrderingPeriodicity["Quarterly"] = "QUARTERLY";
})(ShopOrderingPeriodicity || (ShopOrderingPeriodicity = {}));
var ShopPackageType;
(function (ShopPackageType) {
    ShopPackageType["Category"] = "CATEGORY";
    ShopPackageType["ClientSpecificity"] = "CLIENT_SPECIFICITY";
    ShopPackageType["Default"] = "DEFAULT";
    ShopPackageType["Event"] = "EVENT";
    ShopPackageType["GoodDeals"] = "GOOD_DEALS";
    ShopPackageType["Grant"] = "GRANT";
    ShopPackageType["UniqueBrand"] = "UNIQUE_BRAND";
})(ShopPackageType || (ShopPackageType = {}));
var ShopPurchaseMode;
(function (ShopPurchaseMode) {
    ShopPurchaseMode["Amount"] = "AMOUNT";
    ShopPurchaseMode["Quantity"] = "QUANTITY";
})(ShopPurchaseMode || (ShopPurchaseMode = {}));
var SingleUseTokenError;
(function (SingleUseTokenError) {
    SingleUseTokenError["SsoFailToken"] = "SSO_FAIL_TOKEN";
})(SingleUseTokenError || (SingleUseTokenError = {}));
var SiteStatus;
(function (SiteStatus) {
    SiteStatus["Creating"] = "CREATING";
    SiteStatus["Deleted"] = "DELETED";
    SiteStatus["Error"] = "ERROR";
    SiteStatus["Na"] = "NA";
    SiteStatus["Ready"] = "READY";
})(SiteStatus || (SiteStatus = {}));
var SoldOutType;
(function (SoldOutType) {
    SoldOutType["InStock"] = "IN_STOCK";
    SoldOutType["NoStock"] = "NO_STOCK";
    SoldOutType["Unavailable"] = "UNAVAILABLE";
})(SoldOutType || (SoldOutType = {}));
var SubSegmentUnit;
(function (SubSegmentUnit) {
    SubSegmentUnit["Employee"] = "EMPLOYEE";
    SubSegmentUnit["Eur"] = "EUR";
    SubSegmentUnit["Unknown"] = "UNKNOWN";
})(SubSegmentUnit || (SubSegmentUnit = {}));
var SubscriptionStatus;
(function (SubscriptionStatus) {
    SubscriptionStatus["Active"] = "ACTIVE";
    SubscriptionStatus["Cancelled"] = "CANCELLED";
    SubscriptionStatus["Draft"] = "DRAFT";
    SubscriptionStatus["PendingAcceptance"] = "PENDING_ACCEPTANCE";
    SubscriptionStatus["PendingActivation"] = "PENDING_ACTIVATION";
    SubscriptionStatus["Suspended"] = "SUSPENDED";
})(SubscriptionStatus || (SubscriptionStatus = {}));
var TaskResultType;
(function (TaskResultType) {
    TaskResultType["Csv"] = "CSV";
    TaskResultType["Exception"] = "EXCEPTION";
    TaskResultType["Object"] = "OBJECT";
    TaskResultType["Pdf"] = "PDF";
    TaskResultType["Unknown"] = "UNKNOWN";
    TaskResultType["Xlsx"] = "XLSX";
    TaskResultType["Zip"] = "ZIP";
})(TaskResultType || (TaskResultType = {}));
var TaskStatus;
(function (TaskStatus) {
    TaskStatus["Completed"] = "COMPLETED";
    TaskStatus["Executing"] = "EXECUTING";
    TaskStatus["Failed"] = "FAILED";
    TaskStatus["Finished"] = "FINISHED";
    TaskStatus["Rejected"] = "REJECTED";
    TaskStatus["Starting"] = "STARTING";
    TaskStatus["Unknown"] = "UNKNOWN";
})(TaskStatus || (TaskStatus = {}));
var TermType;
(function (TermType) {
    TermType["Evergreen"] = "EVERGREEN";
    TermType["Termed"] = "TERMED";
})(TermType || (TermType = {}));
var TransactionType;
(function (TransactionType) {
    TransactionType["BankCard"] = "BANK_CARD";
    TransactionType["BankTransfer"] = "BANK_TRANSFER";
    TransactionType["Virtual"] = "VIRTUAL";
})(TransactionType || (TransactionType = {}));
var TwoFactorAuthenticationStatus;
(function (TwoFactorAuthenticationStatus) {
    TwoFactorAuthenticationStatus["Active"] = "ACTIVE";
    TwoFactorAuthenticationStatus["Disabled"] = "DISABLED";
    TwoFactorAuthenticationStatus["Pending"] = "PENDING";
})(TwoFactorAuthenticationStatus || (TwoFactorAuthenticationStatus = {}));
var UnionType;
(function (UnionType) {
    UnionType["Marriage"] = "MARRIAGE";
    UnionType["Pacs"] = "PACS";
    UnionType["Private"] = "PRIVATE";
    UnionType["Single"] = "SINGLE";
    UnionType["Unspecified"] = "UNSPECIFIED";
})(UnionType || (UnionType = {}));
var UpdateBankAccountErrorType;
(function (UpdateBankAccountErrorType) {
    UpdateBankAccountErrorType["AccountNameAlreadyExist"] = "ACCOUNT_NAME_ALREADY_EXIST";
    UpdateBankAccountErrorType["Unknown"] = "UNKNOWN";
})(UpdateBankAccountErrorType || (UpdateBankAccountErrorType = {}));
var UpdateBeneficiaryError;
(function (UpdateBeneficiaryError) {
    UpdateBeneficiaryError["LoginUnavailable"] = "LOGIN_UNAVAILABLE";
})(UpdateBeneficiaryError || (UpdateBeneficiaryError = {}));
var UpdateLogoErrorType;
(function (UpdateLogoErrorType) {
    UpdateLogoErrorType["UnsupportedImageSize"] = "UNSUPPORTED_IMAGE_SIZE";
    UpdateLogoErrorType["UnsupportedImageType"] = "UNSUPPORTED_IMAGE_TYPE";
})(UpdateLogoErrorType || (UpdateLogoErrorType = {}));
var UpdateOrganizationMailLogoErrorType;
(function (UpdateOrganizationMailLogoErrorType) {
    UpdateOrganizationMailLogoErrorType["UnsupportedImageSize"] = "UNSUPPORTED_IMAGE_SIZE";
    UpdateOrganizationMailLogoErrorType["UnsupportedImageType"] = "UNSUPPORTED_IMAGE_TYPE";
})(UpdateOrganizationMailLogoErrorType || (UpdateOrganizationMailLogoErrorType = {}));
var UrssafEventType;
(function (UrssafEventType) {
    UrssafEventType["AdultChristmas"] = "ADULT_CHRISTMAS";
    UrssafEventType["BackToSchool"] = "BACK_TO_SCHOOL";
    UrssafEventType["BirthOrAdoption"] = "BIRTH_OR_ADOPTION";
    UrssafEventType["ChildChristmas"] = "CHILD_CHRISTMAS";
    UrssafEventType["Culture"] = "CULTURE";
    UrssafEventType["FathersDay"] = "FATHERS_DAY";
    UrssafEventType["MothersDay"] = "MOTHERS_DAY";
    UrssafEventType["Retirement"] = "RETIREMENT";
    UrssafEventType["SaintCatherine"] = "SAINT_CATHERINE";
    UrssafEventType["SaintNicolas"] = "SAINT_NICOLAS";
    UrssafEventType["Unrecognized"] = "UNRECOGNIZED";
    UrssafEventType["WeddingOrPacs"] = "WEDDING_OR_PACS";
})(UrssafEventType || (UrssafEventType = {}));
var WalletSorting;
(function (WalletSorting) {
    WalletSorting["Alphabetical"] = "ALPHABETICAL";
    WalletSorting["DiscountRate"] = "DISCOUNT_RATE";
    WalletSorting["Relevance"] = "RELEVANCE";
    WalletSorting["ReverseAlphabetical"] = "REVERSE_ALPHABETICAL";
    WalletSorting["SmallestAmount"] = "SMALLEST_AMOUNT";
})(WalletSorting || (WalletSorting = {}));
var WalletType;
(function (WalletType) {
    WalletType["CultureCard"] = "CULTURE_CARD";
    WalletType["CultureTicket"] = "CULTURE_TICKET";
    WalletType["Discount"] = "DISCOUNT";
    WalletType["GiftCard"] = "GIFT_CARD";
    WalletType["MovieTicket"] = "MOVIE_TICKET";
    WalletType["ParkTicket"] = "PARK_TICKET";
    WalletType["RecreationTicket"] = "RECREATION_TICKET";
    WalletType["TripAndHoliday"] = "TRIP_AND_HOLIDAY";
})(WalletType || (WalletType = {}));
const ModuleSubscriptionsFragmentDoc = gql `
  fragment ModuleSubscriptions on Organization {
    isGiftSubscribed: isModuleSubscribed(moduleCode: "gift")
    isCultureSubscribed: isModuleSubscribed(moduleCode: "culture")
    isDiscountSubscribed: isModuleSubscribed(moduleCode: "discount")
    isTicketingSubscribed: isModuleSubscribed(moduleCode: "ticketing")
    isGestionSubscribed: isModuleSubscribed(moduleCode: "gestion")
    isAccountingSubscribed: isModuleSubscribed(moduleCode: "accounting")
    isSiteSubscribed: isModuleSubscribed(moduleCode: "website")
    isFoodSubscribed: isModuleSubscribed(moduleCode: "food")
    isPaperOrderSubscribed: isModuleSubscribed(moduleCode: "gift_paper")
    isNewsletterSubscribed: isModuleSubscribed(moduleCode: "newsletter")
  }
`;
const CanUserAccessManagerBridgeDocument = gql `
  query CanUserAccessManagerBridge {
    authenticatedUser {
      uiRights {
        canAccessManagerBridge
      }
    }
  }
`;
class CanUserAccessManagerBridgeGQL extends i1$1.Query {
    constructor(apollo) {
        super(apollo);
        this.document = CanUserAccessManagerBridgeDocument;
    }
}
CanUserAccessManagerBridgeGQL.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CanUserAccessManagerBridgeGQL, deps: [{ token: i1$1.Apollo }], target: i0.ɵɵFactoryTarget.Injectable });
CanUserAccessManagerBridgeGQL.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CanUserAccessManagerBridgeGQL, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CanUserAccessManagerBridgeGQL, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Apollo }]; } });
const GetOrganizationInfoDocument = gql `
  query GetOrganizationInfo($orgId: ID!) {
    organization(id: $orgId) {
      id
      ...ModuleSubscriptions
    }
  }
  ${ModuleSubscriptionsFragmentDoc}
`;
class GetOrganizationInfoGQL extends i1$1.Query {
    constructor(apollo) {
        super(apollo);
        this.document = GetOrganizationInfoDocument;
    }
}
GetOrganizationInfoGQL.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationInfoGQL, deps: [{ token: i1$1.Apollo }], target: i0.ɵɵFactoryTarget.Injectable });
GetOrganizationInfoGQL.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationInfoGQL, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationInfoGQL, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Apollo }]; } });
const GetOrganizationSubscriptionInfoDocument = gql `
  query GetOrganizationSubscriptionInfo($orgId: ID!) {
    organization(id: $orgId) {
      id
      subscribedModules {
        functionalCode
      }
    }
  }
`;
class GetOrganizationSubscriptionInfoGQL extends i1$1.Query {
    constructor(apollo) {
        super(apollo);
        this.document = GetOrganizationSubscriptionInfoDocument;
    }
}
GetOrganizationSubscriptionInfoGQL.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationSubscriptionInfoGQL, deps: [{ token: i1$1.Apollo }], target: i0.ɵɵFactoryTarget.Injectable });
GetOrganizationSubscriptionInfoGQL.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationSubscriptionInfoGQL, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GetOrganizationSubscriptionInfoGQL, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Apollo }]; } });

class OrganizationAccessService {
    constructor(getOrganizationInfoGQL, getOrganizationSubscriptionInfoGQL, currentOrganizationService) {
        this.getOrganizationInfoGQL = getOrganizationInfoGQL;
        this.getOrganizationSubscriptionInfoGQL = getOrganizationSubscriptionInfoGQL;
        this.currentOrganizationService = currentOrganizationService;
    }
    /**
     * Ask to get organization subscription information.
     *
     * @param organizationId the organization id.
     */
    getOrganizationSubscriptionInfo() {
        return this.currentOrganizationService.currentOrganizationId$.pipe(switchMap((orgId) => this.getOrganizationInfoGQL.fetch({
            orgId,
        }, { errorPolicy: 'all', fetchPolicy: 'no-cache' })));
    }
    isModuleOnlySubscribed(module) {
        return this.currentOrganizationService.currentOrganizationId$
            .pipe(switchMap((orgId) => this.getOrganizationSubscriptionInfoGQL.fetch({
            orgId,
        }, { errorPolicy: 'all', fetchPolicy: 'no-cache' })))
            .pipe(map((result) => {
            var _a;
            if (result.error) {
                console.error(result.error);
                return false;
            }
            const subscribedModules = (_a = result.data.organization) === null || _a === void 0 ? void 0 : _a.subscribedModules;
            return ((subscribedModules === null || subscribedModules === void 0 ? void 0 : subscribedModules.length) === 1 &&
                subscribedModules[0].functionalCode === module);
        }));
    }
}
OrganizationAccessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationAccessService, deps: [{ token: GetOrganizationInfoGQL }, { token: GetOrganizationSubscriptionInfoGQL }, { token: CurrentOrganizationService }], target: i0.ɵɵFactoryTarget.Injectable });
OrganizationAccessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationAccessService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: OrganizationAccessService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: GetOrganizationInfoGQL }, { type: GetOrganizationSubscriptionInfoGQL }, { type: CurrentOrganizationService }]; } });

class PaperModuleOnlyResolver {
    constructor(organizationAccessService) {
        this.organizationAccessService = organizationAccessService;
    }
    resolve(route, state) {
        return this.organizationAccessService.isModuleOnlySubscribed('gift-paper');
    }
}
PaperModuleOnlyResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PaperModuleOnlyResolver, deps: [{ token: OrganizationAccessService }], target: i0.ɵɵFactoryTarget.Injectable });
PaperModuleOnlyResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PaperModuleOnlyResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PaperModuleOnlyResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: OrganizationAccessService }]; } });

class FoodModuleOnlyResolver {
    constructor(organizationAccessService) {
        this.organizationAccessService = organizationAccessService;
    }
    resolve(route, state) {
        return this.organizationAccessService.isModuleOnlySubscribed('food');
    }
}
FoodModuleOnlyResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FoodModuleOnlyResolver, deps: [{ token: OrganizationAccessService }], target: i0.ɵɵFactoryTarget.Injectable });
FoodModuleOnlyResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FoodModuleOnlyResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FoodModuleOnlyResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: OrganizationAccessService }]; } });

class LanguageService {
    constructor(eventBusService) {
        this.eventBusService = eventBusService;
        this.locale$ = this.eventBusService.connectToChannel(...EVENTS_CHANNELS.localeChannel).receiver;
        this.lang$ = this.locale$.pipe(map((value) => value.split('_')[0]));
    }
}
LanguageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, deps: [{ token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.EventBusService }]; } });

class AdminUiRightResolver {
    constructor(router, eventBusService) {
        this.router = router;
        this.eventBusService = eventBusService;
    }
    resolve(route, state) {
        return this.getAdminUiRights();
    }
    getAdminUiRights() {
        return this.eventBusService
            .connectToChannel(...EVENTS_CHANNELS.adminRightChannel)
            .receiver.pipe(map((value) => value), take(1));
    }
}
AdminUiRightResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdminUiRightResolver, deps: [{ token: i1$2.Router }, { token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
AdminUiRightResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdminUiRightResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdminUiRightResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i1.EventBusService }]; } });

class CustomizationDataService {
    constructor(eventBusService) {
        this.eventBusService = eventBusService;
        this.customizationData$ =
            this.eventBusService.connectToChannel(...EVENTS_CHANNELS.customizationDataChannel).receiver;
    }
}
CustomizationDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomizationDataService, deps: [{ token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomizationDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomizationDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomizationDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.EventBusService }]; } });

class AccessResolver {
    constructor(organizationAccessService) {
        this.organizationAccessService = organizationAccessService;
    }
    resolve(route, state) {
        return this.organizationAccessService
            .getOrganizationSubscriptionInfo()
            .pipe(map((result) => {
            if (result.error) {
                console.error(result.error);
                return null;
            }
            return this.loadTrueOrganizationAccess(result, this.getDefaultOrganizationAccess());
        }), first());
    }
    loadTrueOrganizationAccess(result, orgaAccess) {
        if (result.data.organization) {
            for (const fetchedOrgaAccessKey of Object.keys(result.data.organization)) {
                if (fetchedOrgaAccessKey.startsWith('is')) {
                    for (const defaultOrgaAccessKey of Object.keys(orgaAccess)) {
                        if (defaultOrgaAccessKey === fetchedOrgaAccessKey) {
                            orgaAccess[defaultOrgaAccessKey] =
                                result.data.organization[fetchedOrgaAccessKey];
                        }
                    }
                }
            }
        }
        return orgaAccess;
    }
    getDefaultOrganizationAccess() {
        return {
            isAccountingSubscribed: false,
            isGiftSubscribed: false,
            isCultureSubscribed: false,
            isDiscountSubscribed: false,
            isGestionSubscribed: false,
            isSiteSubscribed: false,
            isTicketingSubscribed: false,
            isFoodSubscribed: false,
            isPaperOrderSubscribed: false,
        };
    }
}
AccessResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessResolver, deps: [{ token: OrganizationAccessService }], target: i0.ɵɵFactoryTarget.Injectable });
AccessResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: OrganizationAccessService }]; } });

// In single-spa, the assets need to be loaded from a dynamic location,
// instead of hard coded to `/assets`. We use webpack public path for this.
// See https://webpack.js.org/guides/public-path/#root
const assetUrl = (url) => {
    // @ts-ignore
    const publicPath = __webpack_public_path__;
    const publicPathSuffix = publicPath.endsWith('/') ? '' : '/';
    const urlPrefix = url.startsWith('/') ? '' : '/';
    return `${publicPath}${publicPathSuffix}assets${urlPrefix}${url}`;
};

class TranslocoHttpLoader {
    constructor(http) {
        this.http = http;
    }
    getTranslation(lang) {
        const microFrontTranslation$ = this.http.get(assetUrl(`/i18n/${lang}.json`));
        const commonTranslation$ = this.http
            .get(`/common/assets/i18n/${lang}.brand.json`)
            .pipe(catchError((err) => {
            console.warn(`Unable to parse /common/assets/i18n/${lang}.brand.json file (branded translations)!`);
            return of({});
        }));
        return combineLatest([microFrontTranslation$, commonTranslation$]).pipe(map(([microFrontTranslation, commonTranslation]) => {
            return Object.assign(Object.assign({}, microFrontTranslation), commonTranslation);
        }));
    }
}
TranslocoHttpLoader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoHttpLoader, deps: [{ token: i1$3.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
TranslocoHttpLoader.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoHttpLoader, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoHttpLoader, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }]; } });
class TranslocoRootModule {
}
TranslocoRootModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoRootModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TranslocoRootModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: TranslocoRootModule, exports: [TranslocoModule] });
TranslocoRootModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoRootModule, providers: [
        {
            provide: TRANSLOCO_CONFIG,
            useValue: translocoConfig({
                availableLangs: ['fr', 'en', 'nl'],
                defaultLang: getBrowserLang(),
                fallbackLang: 'fr',
                missingHandler: {
                    useFallbackTranslation: true,
                    logMissingKey: false,
                },
                // Remove this option if your application doesn't support changing language in runtime.
                reRenderOnLangChange: true,
                prodMode: true,
            }),
        },
        { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
    ], imports: [TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TranslocoRootModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [TranslocoModule],
                    providers: [
                        {
                            provide: TRANSLOCO_CONFIG,
                            useValue: translocoConfig({
                                availableLangs: ['fr', 'en', 'nl'],
                                defaultLang: getBrowserLang(),
                                fallbackLang: 'fr',
                                missingHandler: {
                                    useFallbackTranslation: true,
                                    logMissingKey: false,
                                },
                                // Remove this option if your application doesn't support changing language in runtime.
                                reRenderOnLangChange: true,
                                prodMode: true,
                            }),
                        },
                        { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
                    ],
                }]
        }] });

class ManagerProxiResolver {
    constructor(canUserAccessManagerBridgeGQL) {
        this.canUserAccessManagerBridgeGQL = canUserAccessManagerBridgeGQL;
    }
    resolve() {
        return this.canUserAccessManagerBridgeGQL
            .fetch({})
            .pipe(map((v) => v.data.authenticatedUser.uiRights.canAccessManagerBridge));
    }
}
ManagerProxiResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ManagerProxiResolver, deps: [{ token: CanUserAccessManagerBridgeGQL }], target: i0.ɵɵFactoryTarget.Injectable });
ManagerProxiResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ManagerProxiResolver });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ManagerProxiResolver, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CanUserAccessManagerBridgeGQL }]; } });

/**
 * Allow microfronts to send tracking events to Amplitude via GTM.
 * Drop-in substitute for TrackingService (from front-common-lib)
 * for microfronts ONLY.
 */
class PortalTrackingService {
    constructor(eventBus) {
        this.eventBus = eventBus;
        const channel = eventBus.connectToChannel(...EVENTS_CHANNELS.trackingChannel);
        this.trackingEventEmitter = channel.emitter;
        this.trackingReceiver$ = channel.receiver;
    }
    /**
     * Send a tracking event.
     *
     * @param event
     */
    sendEvent(event) {
        this.trackingEventEmitter.next(event);
    }
}
PortalTrackingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortalTrackingService, deps: [{ token: i1.EventBusService }], target: i0.ɵɵFactoryTarget.Injectable });
PortalTrackingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortalTrackingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortalTrackingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.EventBusService }]; } });

/*
 * Public API Surface
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccessResolver, AdminUiRightResolver, AuthGuard, AuthInfoType, AuthModule, AuthService, CurrentOrganizationResolver, CurrentOrganizationService, CustomizationDataService, EVENTS_CHANNELS, FoodModuleOnlyResolver, LanguageService, ManagerProxiResolver, OrganizationAccessService, OrganizationModule, PaperModuleOnlyResolver, PortalTrackingService, TranslocoHttpLoader, TranslocoRootModule, isAuthenticated };
